/**
 * Applications Service
 * 
 * Handles all application-related API calls.
 * Manages CRUD operations for apps and user permissions.
 * 
 * @module services/apps
 * 
 * @example
 * // Get user's authorized apps
 * const myApps = await appsService.getMyApps();
 * 
 * @example
 * // Create a new app (admin only)
 * const newApp = await appsService.create({
 *   appName: 'My App',
 *   appUrl: 'https://myapp.haat.delivery'
 * });
 */


// 2026-06-22 10:00:00

import { apiClient, ApiResponse } from './api';
import { App, CreateAppDto, UpdateAppDto } from '../types';

// =============================================================================
// APPS SERVICE
// =============================================================================

/**
 * Applications service object
 * 
 * Contains all application-related methods.
 * Some methods require admin permissions.
 */
export const appsService = {
    // ===========================================================================
    // READ OPERATIONS (All Users)
    // ===========================================================================

    /**
     * Get apps authorized for current user
     * 
     * Returns only apps that the current user has access to.
     * This is the primary method for the dashboard.
     * 
     * @returns Promise resolving to list of authorized apps
     * 
     * @example
     * const myApps = await appsService.getMyApps();
     * myApps.forEach(app => console.log(app.appName));
     */
    getMyApps: async (): Promise<App[]> => {
        const response = await apiClient.get<ApiResponse<App[]>>('/permissions/my-apps');
        return response.data.data;
    },

    /**
     * Toggle favorite state for a hub app (current user).
     * @param appId - Hub app ID
     * @returns New favorite state
     */
    toggleHubAppFavorite: async (appId: number): Promise<{ appId: number; isFavorite: boolean }> => {
        const response = await apiClient.patch<ApiResponse<{ appId: number; isFavorite: boolean }>>(
            `/permissions/my-apps/${appId}/favorite`,
        );
        return response.data.data;
    },

    /**
     * Get a single app by ID
     * 
     * @param id - Application ID
     * @returns Promise resolving to app data
     * @throws Error if app not found (404)
     * 
     * @example
     * const app = await appsService.getById(1);
     * console.log('App URL:', app.appUrl);
     */
    getById: async (id: number): Promise<App> => {
        const response = await apiClient.get<ApiResponse<App>>(`/apps/${id}`);
        return response.data.data;
    },

    // ===========================================================================
    // READ OPERATIONS (Admin Only)
    // ===========================================================================

    /**
     * Get all apps (including inactive)
     * 
     * @requires Admin role
     * @returns Promise resolving to list of all apps
     * 
     * @example
     * // Admin dashboard - show all apps
     * const allApps = await appsService.getAll();
     */
    getAll: async (): Promise<App[]> => {
        const response = await apiClient.get<ApiResponse<App[]>>('/apps/all');
        return response.data.data;
    },

    /**
     * Get only active apps
     * 
     * @returns Promise resolving to list of active apps
     * 
     * @example
     * const activeApps = await appsService.getActive();
     */
    getActive: async (): Promise<App[]> => {
        const response = await apiClient.get<ApiResponse<App[]>>('/apps');
        return response.data.data;
    },

    // ===========================================================================
    // WRITE OPERATIONS (Admin Only)
    // ===========================================================================

    /**
     * Create a new application
     * 
     * @requires Admin role
     * @param data - Application data
     * @returns Promise resolving to created app
     * 
     * @example
     * const newApp = await appsService.create({
     *   appName: 'Taskify',
     *   appDescription: 'Task management',
     *   appUrl: 'https://taskify.haat.delivery',
     *   appIcon: 'check-square',
     *   appColor: '#4CAF50'
     * });
     */
    create: async (data: CreateAppDto): Promise<App> => {
        const response = await apiClient.post<ApiResponse<App>>('/apps', data);
        return response.data.data;
    },

    /**
     * Update an existing application
     * 
     * @requires Admin role
     * @param id - Application ID to update
     * @param data - Fields to update
     * @returns Promise resolving to updated app
     * 
     * @example
     * const updatedApp = await appsService.update(1, {
     *   appDescription: 'Updated description'
     * });
     */
    update: async (id: number, data: UpdateAppDto): Promise<App> => {
        const response = await apiClient.patch<ApiResponse<App>>(`/apps/${id}`, data);
        return response.data.data;
    },

    /**
     * Delete an application
     * 
     * @requires Super Admin role
     * @param id - Application ID to delete
     * @returns Promise that resolves when deleted
     * 
     * @example
     * await appsService.delete(1);
     * console.log('App deleted');
     */
    delete: async (id: number): Promise<void> => {
        await apiClient.delete(`/apps/${id}`);
    },

    /**
     * Toggle app active/inactive status
     * 
     * @requires Admin role
     * @param id - Application ID
     * @returns Promise resolving to updated app
     * 
     * @example
     * const app = await appsService.toggleActive(1);
     * console.log('App is now:', app.isActive ? 'active' : 'inactive');
     */
    toggleActive: async (id: number): Promise<App> => {
        const response = await apiClient.patch<ApiResponse<App>>(`/apps/${id}/toggle-active`);
        return response.data.data;
    },
};

// =============================================================================
// EXPORTS
// =============================================================================

// Re-export types for convenience
export type { App, CreateAppDto, UpdateAppDto } from '../types';

export default appsService;
